import { createSignal } from 'solid-js';

/**
 * Voice Notification Settings Store
 *
 * Configure voice announcements for real-time conversion notifications.
 *
 * AVAILABLE SETTINGS OPTIONS:
 * ===========================
 *
 * 1. ENABLED (boolean)
 *    - Enable or disable voice announcements completely
 *    - Default: true
 *    - When disabled, only visual toast notifications show
 *    - Storage key: 'voiceNotifications.enabled'
 *
 * 2. SPEECH_RATE (number: 0.5 - 2.0)
 *    - Control how fast the voice speaks
 *    - 0.5 = very slow, 1.0 = normal, 2.0 = very fast
 *    - Default: 1.0 (normal speed)
 *    - Storage key: 'voiceNotifications.rate'
 *    - Recommended: 0.8 - 1.2 for clarity
 *
 * 3. PITCH (number: 0.5 - 2.0)
 *    - Control the pitch/tone of the voice
 *    - 0.5 = very low, 1.0 = normal, 2.0 = very high
 *    - Default: 1.0 (normal pitch)
 *    - Storage key: 'voiceNotifications.pitch'
 *    - Recommended: 0.8 - 1.2 for natural sound
 *
 * 4. VOLUME (number: 0.0 - 1.0)
 *    - Control the volume/loudness of the voice
 *    - 0.0 = mute, 0.5 = half volume, 1.0 = full volume
 *    - Default: 1.0 (full volume)
 *    - Storage key: 'voiceNotifications.volume'
 *    - Recommended: 0.7 - 1.0
 *
 * 5. ANNOUNCEMENT_FORMAT (string)
 *    - Customize the announcement message format
 *    - Default: 'full' - "Conversion received from {user} for {payout} dollars"
 *    - Options:
 *      * 'full' - Complete message with user and payout
 *      * 'short' - Brief message "Conversion from {user}"
 *      * 'payout_only' - Just the amount "{payout} dollars"
 *      * 'user_only' - Just the user name "{user}"
 *    - Storage key: 'voiceNotifications.format'
 *
 * 6. VOICE_SELECTION (string)
 *    - Select which voice to use (browser dependent)
 *    - Default: '' (browser default)
 *    - Browser determines available voices
 *    - Common options: 'male', 'female', 'neutral'
 *    - Storage key: 'voiceNotifications.voice'
 *    - Note: Availability varies by browser and OS
 *
 * 7. AUTO_DISMISS_TOAST (boolean)
 *    - Whether toast dismisses automatically
 *    - Default: true
 *    - Duration: 4000ms (4 seconds)
 *    - Storage key: 'voiceNotifications.autoDismiss'
 *
 * 8. SOUND_ENABLED (boolean)
 *    - Enable/disable sound notifications
 *    - Default: true
 *    - If false, only visual toast shows (no voice)
 *    - Storage key: 'voiceNotifications.soundEnabled'
 *
 * 9. VIBRATION_FEEDBACK (boolean)
 *    - Enable device vibration on conversion (mobile)
 *    - Default: true
 *    - Works on devices with vibration API
 *    - Storage key: 'voiceNotifications.vibration'
 *
 * 10. LANGUAGE (string)
 *     - Language for voice announcements
 *     - Default: 'en-US' (English - US)
 *     - Common options: 'en-US', 'en-GB', 'es-ES', 'fr-FR', 'de-DE', 'ja-JP'
 *     - Storage key: 'voiceNotifications.language'
 *     - Browser determines available languages
 *
 * USAGE EXAMPLE:
 * ==============
 * import { getVoiceSettings, updateVoiceSettings } from './voiceNotificationStore';
 *
 * // Get current settings
 * const settings = getVoiceSettings();
 * console.log(settings.enabled); // true
 *
 * // Update settings
 * updateVoiceSettings({
 *   enabled: true,
 *   rate: 0.9,
 *   pitch: 1.0,
 *   volume: 0.8,
 *   format: 'full',
 *   soundEnabled: true
 * });
 */

export interface VoiceNotificationSettings {
  // Enable/disable voice announcements
  enabled: boolean;

  // Speech rate (0.5 - 2.0, default: 1.0)
  rate: number;

  // Voice pitch (0.5 - 2.0, default: 1.0)
  pitch: number;

  // Voice volume (0.0 - 1.0, default: 1.0)
  volume: number;

  // Announcement format ('full' | 'short' | 'payout_only' | 'user_only')
  format: 'full' | 'short' | 'payout_only' | 'user_only';

  // Voice selection (browser dependent)
  voice: string;

  // Auto-dismiss toast notifications
  autoDismiss: boolean;

  // Enable/disable sound
  soundEnabled: boolean;

  // Device vibration feedback (mobile)
  vibration: boolean;

  // Language code (e.g., 'en-US', 'es-ES')
  language: string;
}

// Default settings
const DEFAULT_SETTINGS: VoiceNotificationSettings = {
  enabled: true,
  rate: 1.0,
  pitch: 1.0,
  volume: 1.0,
  format: 'full',
  voice: '',
  autoDismiss: true,
  soundEnabled: true,
  vibration: true,
  language: 'id-ID',
};

const STORAGE_KEY = 'voiceNotifications';

/**
 * Load voice notification settings from localStorage
 * Falls back to default settings if not found or invalid
 */
const getInitialSettings = (): VoiceNotificationSettings => {
  if (typeof window === 'undefined') return DEFAULT_SETTINGS;

  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (!stored) return DEFAULT_SETTINGS;

    const parsed = JSON.parse(stored);
    // Merge with defaults to handle missing fields
    return { ...DEFAULT_SETTINGS, ...parsed };
  } catch (error) {
    return DEFAULT_SETTINGS;
  }
};

// Create reactive signal for voice notification settings
const [voiceSettings, setVoiceSettings] = createSignal<VoiceNotificationSettings>(
  getInitialSettings()
);

/**
 * Save settings to localStorage
 */
const saveSettings = (settings: VoiceNotificationSettings) => {
  if (typeof window === 'undefined') return;

  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(settings));
  } catch (error) {
    // Error saving settings
  }
};

/**
 * Get the current voice notification settings value
 */
export const getVoiceSettings = (): VoiceNotificationSettings => voiceSettings();

/**
 * Update voice notification settings
 * Accepts partial settings object, merges with existing settings
 */
export const updateVoiceSettings = (newSettings: Partial<VoiceNotificationSettings>) => {
  const currentSettings = voiceSettings();
  const updatedSettings = { ...currentSettings, ...newSettings };
  setVoiceSettings(updatedSettings);
  saveSettings(updatedSettings);
};

/**
 * Reset voice notification settings to defaults
 */
export const resetVoiceSettings = () => {
  setVoiceSettings(DEFAULT_SETTINGS);
  saveSettings(DEFAULT_SETTINGS);
};

/**
 * Build announcement message based on configured format
 */
export const buildAnnouncementMessage = (user: string, payout: number, format?: string): string => {
  const selectedFormat = format || voiceSettings().format;
  const formattedPayout = payout.toFixed(2).replace('.', ' koma ');

  switch (selectedFormat) {
    case 'short':
      return `${user}`;
    case 'payout_only':
      return formattedPayout;
    case 'user_only':
      return user;
    case 'full':
    default:
      return `${user} dapat ${formattedPayout}`;
  }
};

export default {
  getVoiceSettings,
  updateVoiceSettings,
  resetVoiceSettings,
  buildAnnouncementMessage,
};
